<template>
  <div class="f-radios-wrap" ref="multipleWrapper">
    <ul
      class="f-radios"
      v-bind:class="{ 'f-multiple': question.multiple }"
      role="listbox"
      v-if="isMobile"
    >
      <li
        v-for="(option, index) in question.options"
        v-on:click.prevent="toggleAnswer(option)"
        v-bind:class="{ 'f-selected': option.selected }"
        v-bind:key="'m' + index"
        v-bind:aria-label="getLabel(index)"
        role="option"
        class="mb-1 mobile-list"
      >
        <div class="f-key"><span class="bullet"></span></div>
        <span class="f-label">{{ option.choiceLabel() }}</span>
      </li>
      <li
        class="f-other"
        v-if="question.allowOther"
        v-on:click.prevent="startEditOther"
        v-bind:class="{ 'f-selected': question.other, 'f-focus': editingOther }"
        v-bind:aria-label="language.ariaTypeAnswer"
        role="option"
      >
        <span class="f-key" v-if="!editingOther">{{
          getToggleKey(question.options.length)
        }}</span>
        <input
          v-if="editingOther"
          v-model="question.other"
          type="text"
          ref="otherInput"
          v-on:blur="stopEditOther"
          v-on:keyup.enter.prevent="stopEditOther"
          v-on:keyup="onChangeOther"
          v-on:change="onChangeOther"
          maxlength="256"
        />
        <span v-else-if="question.other" class="f-selected">
          <span class="f-label">{{ question.other }}</span>
        </span>
        <span v-else class="f-label">{{ language.otherPrompt }}</span>
      </li>
    </ul>
    <div class="option-wrapper" v-else>
      <div class="odd">
        <ul
          class="f-radios"
          v-bind:class="{ 'f-multiple': question.multiple }"
          role="listbox"
        >
          <li
            v-for="(option, index) in oddOptions"
            v-on:click.prevent="toggleAnswer(option)"
            v-bind:class="{ 'f-selected': option.selected }"
            v-bind:key="'m' + index"
            v-bind:aria-label="option.label"
            role="option"
            class="oddlist"
          >
            <div class="f-key"><span class="bullet"></span></div>
            <span class="f-label">{{ option.choiceLabel() }}</span>
          </li>
        </ul>
      </div>
      <div class="even">
        <ul
          class="f-radios"
          v-bind:class="{ 'f-multiple': question.multiple }"
          role="listbox"
        >
          <li
            v-for="(option, index) in evenOptions"
            v-on:click.prevent="toggleAnswer(option)"
            v-bind:class="{ 'f-selected': option.selected }"
            v-bind:key="'m' + index"
            v-bind:aria-label="option.label"
            role="option"
            class="evenlist"
          >
            <div class="f-key"><span class="bullet"></span></div>
            <span class="f-label">{{ option.choiceLabel() }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import BaseType from "./BaseType.vue";
import { QuestionType } from "../../models/QuestionModel";
import { IsMobile } from "../../mixins/IsMobile";

export default {
  extends: BaseType,
  name: QuestionType.MultipleChoice,
  mixins: [IsMobile],

  data() {
    return {
      editingOther: false
    };
  },

  computed: {
    evenOptions() {
      return this.question.options.filter((item, index) => index % 2);
    },
    oddOptions() {
      return this.question.options.filter((item, index) => !(index % 2));
    }
  },

  mounted() {
    if (this.question.multiple) {
      this.dataValue = [];
    }

    this.addKeyListener();
  },

  beforeDestroy() {
    this.removeKeyListener();
  },

  watch: {
    active(value) {
      if (value) {
        this.addKeyListener();

        if (this.question.multiple && this.question.answered) {
          this.enterPressed = false;
        }
      } else {
        this.removeKeyListener();
      }
    }
  },

  methods: {
    addKeyListener() {
      this.removeKeyListener();
      document.addEventListener("keyup", this.onKeyListener);
    },

    removeKeyListener() {
      document.removeEventListener("keyup", this.onKeyListener);
    },

    /**
     * Listens for keyboard events (A, B, C, ...)
     */
    onKeyListener($event) {
      if (
        this.active &&
        !this.editingOther &&
        $event.key &&
        $event.key.length === 1
      ) {
        let keyCode = $event.key.toUpperCase().charCodeAt(0);

        if (keyCode >= 65 && keyCode <= 90) {
          let index = keyCode - 65;

          if (index > -1) {
            let option = this.question.options[index];

            if (option) {
              this.toggleAnswer(option);
            } else if (
              this.question.allowOther &&
              index === this.question.options.length
            ) {
              this.startEditOther();
            }
          }
        }
      }
    },

    getLabel(index) {
      return this.language.ariaMultipleChoice.replace(
        ":letter",
        this.getToggleKey(index)
      );
    },

    getToggleKey(index) {
      const key = 65 + index;

      if (key <= 90) {
        return String.fromCharCode(key);
      }

      return "";
    },

    toggleAnswer(option) {
      if (!this.question.multiple) {
        if (this.question.allowOther) {
          this.question.other = this.dataValue = null;
          this.setAnswer(this.dataValue);
        }

        for (let i = 0; i < this.question.options.length; i++) {
          let o = this.question.options[i];

          if (o.selected) {
            this._toggleAnswer(o);
          }
        }
      }

      this._toggleAnswer(option);
    },

    _toggleAnswer(option) {
      option.toggle();

      if (this.question.multiple) {
        this.enterPressed = false;

        if (!option.selected) {
          this._removeAnswer(option.choiceValue());
        } else {
          this.dataValue.push(option.choiceValue());
        }
      } else {
        this.dataValue = option.selected ? option.choiceValue() : null;
      }

      this.setAnswer(this.dataValue);

      if (
        this.isValid() &&
        this.question.nextStepOnAnswer &&
        !this.question.multiple
      ) {
        setTimeout(() => this.$emit("next"), 350);
      }
    },

    _removeAnswer(value) {
      const index = this.dataValue.indexOf(value);

      if (index !== -1) {
        this.dataValue.splice(index, 1);
      }
    },

    startEditOther() {
      this.editingOther = true;
      this.enterPressed = false;

      this.$nextTick(() => {
        this.$refs.otherInput.focus();
      });
    },

    onChangeOther() {
      if (this.editingOther) {
        let value = [],
          self = this;

        this.question.options.forEach(function(option) {
          if (option.selected) {
            if (self.question.multiple) {
              value.push(option.choiceValue());
            } else {
              option.toggle();
            }
          }
        });

        if (this.question.other && this.question.multiple) {
          value.push(this.question.other);
        } else if (!this.question.multiple) {
          value = this.question.other;
        }

        this.dataValue = value;
        this.setAnswer(this.dataValue);
      }
    },

    stopEditOther() {
      this.editingOther = false;
    }
  }
};
</script>

<style scoped>
.option-wrapper {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: row;
}

.mobile-list {
  display: flex;
  align-items: center;
}

.oddlist {
  margin: 8px 8px 16px 0px;
  display: flex;
  align-items: center;
  cursor: pointer !important;
}

.evenlist {
  margin: 8px 0px 16px 8px;
  display: flex;
  align-items: center;
  cursor: pointer !important;
}

.mb-1 {
  margin-bottom: 8px;
}

.odd,
.even {
  width: 50%;
}

.bullet {
  width: 10px;
  height: 10px;
  border-radius: 5px;
}

.f-selected .f-key .bullet {
  background-color: white;
}

.f-selected .f-key {
  border: 1px solid white;
}

.f-key {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  float: left;
  border-radius: 8px;
}
</style>
